import { useEffect, useRef } from 'react'
import { Howl, Howler } from 'howler'
import { useAudioStore, type AmbientTrack } from '@/stores/audioStore'

/**
 * Immersive ambient audio engine. Reads volume/track/mute from the audio store
 * (persisted) and drives Howler. Tracks are lazy-loaded; missing files fail
 * silently so the site works before audio assets are licensed/added.
 *
 * Drop licensed loops into /public/audio/<track>.mp3 (see Style Guide §Audio).
 */
const TRACK_SRC: Record<Exclude<AmbientTrack, 'none'>, string> = {
  'eternal-twilight': '/audio/eternal-twilight.mp3',
  tavern: '/audio/tavern.mp3',
  'dungeon-crawl': '/audio/dungeon-crawl.mp3',
  encyclopedia: '/audio/encyclopedia.mp3',
}

export function AudioEngine() {
  const { muted, musicVolume, currentTrack } = useAudioStore()
  const howlRef = useRef<Howl | null>(null)
  const loadedTrack = useRef<AmbientTrack | null>(null)

  // Swap track when it changes
  useEffect(() => {
    if (currentTrack === 'none' || muted) {
      howlRef.current?.stop()
      return
    }
    if (loadedTrack.current !== currentTrack) {
      howlRef.current?.unload()
      const src = TRACK_SRC[currentTrack]
      howlRef.current = new Howl({ src: [src], loop: true, volume: musicVolume, html5: true, onloaderror: () => {} })
      loadedTrack.current = currentTrack
      howlRef.current.play()
    } else if (!howlRef.current?.playing()) {
      howlRef.current?.play()
    }
  }, [currentTrack, muted]) // eslint-disable-line react-hooks/exhaustive-deps

  // Volume + global mute
  useEffect(() => {
    Howler.mute(muted)
    howlRef.current?.volume(musicVolume)
  }, [muted, musicVolume])

  useEffect(() => {
    return () => {
      howlRef.current?.unload()
    }
  }, [])
  return null
}

/** Imperative one-shot SFX (dice, coins, page turns). Fails silently if missing. */
export function playSfx(name: 'dice' | 'coin' | 'page' | 'door', volume = 0.6) {
  const s = new Howl({ src: [`/audio/sfx/${name}.mp3`], volume, html5: true, onloaderror: () => {} })
  s.play()
}
